const maxCount = 1000

function createCount (platform, hostname, route, before) {
  return createFindAllOrCount(platform, hostname, route, before, 'count')
}

function createFind (platform, hostname, route, before) {
  const func = async function find (key) {
    let options = {}

    if (key && typeof key !== 'string') {
      options = key
    }

    if (typeof before === 'function') {
      await before(options)
    }

    if (options.where) {
      const findAll = createFindAll(platform, hostname, route)
      const resources = await findAll({ ...options, extract: true })

      return resources.pop()
    }

    if (!platform.storeData.isOnline) {
      const cached = getCache(route)

      if (!key) {
        return Promise.resolve(platform.api.core.copyObject(cached))
      }

      const predicate = createFilterPredicate(key)

      return Promise.resolve(platform.api.core.copyObject(cached.resources.find(predicate)))
    }

    if (key) {
      key = `/${key}`
    } else {
      key = ''
    }

    const res = await platform.fetch(`https://${hostname}${route}${key}`)

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  func.route = route

  return func
}

function createFindAll (platform, hostname, route, before) {
  return createFindAllOrCount(platform, hostname, route, before, 'findAll')
}

function createFindAllPages (platform, hostname, route, before) {
  const func = async function findAllPages (options = {}) {
    if (options.limit) {
      console.warn(`${route}: the limit option in findAllPages calls is ignored`)
    }

    let resources = []

    const optionsCopy = JSON.parse(JSON.stringify(options))

    const func = createFindAll(platform, hostname, route, before)
    let res = await func({
      ...optionsCopy,
      extract: false,
      limit: {
        offset: 0,
        count: maxCount,
      },
    })

    resources = res.resources

    let total = res.total
    let offset = 1

    const copy = JSON.parse(JSON.stringify(options))

    while (total > maxCount) {
      res = await func({
        ...copy,
        extract: false,
        limit: {
          offset: maxCount * offset,
          count: maxCount,
        },
      })

      offset++
      resources = resources.concat(res.resources)
      total = total - maxCount
    }

    if (options.extract) {
      return resources
    }

    return {
      resources,
      total: resources.length,
      offset: 0,
      limit: resources.length,
      count: resources.length,
    }
  }

  func.route = route

  return func
}

function createFindAllOrCount (platform, hostname, route, before, type) {
  const isCount = type === 'count'
  const isFindAll = type === 'findAll'

  if (!isCount && !isFindAll) {
    throw new Error(`Unknown factory type ${type}`)
  }

  if (isCount) {
    route = `${route}/count`
  }

  const func = async function findAllOrCount (options = {}) {
    if (options.params && !Array.isArray(options.params)) {
      throw new Error('params option must be an array')
    }

    if (platform.extractWarn && !options.extract && !options.meta) {
      console.warn([
        `${route}:`,
        `extract should be set to true, or if pagination data is needed then`,
        `meta should be set to true instead`,
      ].join(' '))
    }

    if (typeof before === 'function') {
      await before(options)
    }

    const queryParams = new URLSearchParams(options.params?.join('&'))

    if (options.where) {
      if (typeof options.where !== 'string') {
        throw new Error('where option must be a string')
      }

      queryParams.set('where', options.where)
      queryParams.delete('where-args')

      if (options.whereArgs) {
        const { query, args } = createArgs(options.where, options.whereArgs)

        queryParams.set('where', query)
        queryParams.set('where-args', args.join(','))
      }
    }

    if (options.order) {
      if (typeof options.order !== 'string') {
        throw new Error('order option must be a string')
      }

      queryParams.set('order', options.order)
      queryParams.delete('order-args')

      if (options.orderArgs) {
        const { query, args } = createArgs(options.order, options.orderArgs)

        queryParams.set('order', query)
        queryParams.set('order-args', args.join(','))
      }
    }

    if (options.group && typeof options.group === 'string') {
      queryParams.set('group', options.group)
    }

    if (options.limit && (options.limit.offset || options.limit.count)) {
      let offset = Number(options.limit.offset) || 0
      let count = Number(options.limit.count) || 10

      if (offset < 0) {
        offset = 0
      }

      if (count < 0) {
        count = 10
      }

      if (count > maxCount) {
        console.warn([
          `${route}:`,
          `the maximum number of resources that can be fetched in a single`,
          `request is ${maxCount}, but this request is attempting to fetch ${count}`,
        ].join(' '))
      }

      queryParams.set('limit', `${offset},${count}`)
    }

    let queryString = queryParams.toString()

    if (queryString) {
      queryString = `?${queryString}`
    }

    if (!platform.storeData.isOnline) {
      if (isCount) {
        return 0
      }

      const cached = platform.api.core.copyObject(getCache(route))
      const predicate = createFilterPredicate(options)

      if (predicate) {
        cached.resources = cached.resources.filter(predicate)
      }

      const sort = createSort(options)

      if (sort) {
        cached.resources.sort(sort)
      }

      if (options.extract) {
        return cached.resources
      }

      return Promise.resolve(platform.api.core.copyObject(cached))
    }

    const res = await platform.fetch(`https://${hostname}${route}${queryString}`)

    await maybeThrowAPIError(res, route)

    if (isCount) {
      const { value } = await res.json()

      return value
    }

    if (options.extract) {
      const paginated = await res.json()

      return paginated.resources
    }

    return res.json()
  }

  func.route = route

  return func
}

function createCreate (platform, hostname, route) {
  const func = async function create (data, options = {}) {
    if (options.params && !Array.isArray(options.params)) {
      throw new Error('params option must be an array')
    }

    const queryParams = new URLSearchParams(options.params?.join('&'))

    let queryString = queryParams.toString()

    if (queryString) {
      queryString = `?${queryString}`
    }

    if (!platform.storeData.isOnline) {
      // We don't want to just re-assign data to be an array here because we
      // need the API to behave according to the actual data type the user
      // wanted to pass up
      // This is just being done here for convenience in offline mode
      const dataArray = Array.isArray(data) ? data : [data]
      const cached = getCache(route)

      for (const datum of dataArray) {
        if (!datum.id) {
          datum.id = platform.api.core.uuid()
        }

        cached.resources.push(platform.api.core.copyObject(datum))
        cached.total++
        cached.limit++
        cached.count++
      }

      platform.store.update(store => {
        store.offlineActions.push(() => create(platform.api.core.copyObject(data)))

        return store
      })

      return Promise.resolve(platform.api.core.copyObject(data))
    }

    const res = await platform.fetch(`https://${hostname}${route}${queryString}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  func.route = route

  return func
}

function createUpdate (platform, hostname, route) {
  const func = async function update (data) {
    if (!platform.storeData.isOnline) {
      // We don't want to just re-assign data to be an array here because we
      // need the API to behave according to the actual data type the user
      // wanted to pass up
      // This is just being done here for convenience in offline mode
      const dataArray = Array.isArray(data) ? data : [data]
      const cached = getCache(route)
      let returnData = []

      // This loop is separated from the rest of the processing in offline mode
      // because we only want to process the entire array if there are no errors
      for (const datum of dataArray) {
        if (!datum.id) {
          throw new Error(`${route}: no id provided`)
        }
      }

      for (const datum of dataArray) {
        const predicate = createFilterPredicate(datum.id)
        const index = cached.resources.findIndex(predicate)
        const resource = cached.resources[index]

        cached.resources[index] = platform.api.core.copyObject({ ...resource, ...datum })

        returnData.push(cached.resources[index])
      }

      platform.store.update(store => {
        store.offlineActions.push(() => update(platform.api.core.copyObject(data)))

        return store
      })

      // Since we process everything in offline mode as an array for convenience
      // we need to make sure to return a single object if it's what the user
      // provided to us in the first place
      if (!Array.isArray(data)) {
        returnData = returnData.pop()
      }

      return Promise.resolve(platform.api.core.copyObject(returnData))
    }

    let path = ''

    if (!Array.isArray(data) && data.id) {
      path = `/${data.id}`
    }

    const res = await platform.fetch(`https://${hostname}${route}${path}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  func.route = route

  return func
}

function createUpsert (platform, hostname, route, before) {
  const func = async function upsert (data, options = {}) {
    if (typeof before === 'function') {
      await before(options)
    }

    if (!platform.storeData.isOnline) {
      // We don't want to just re-assign data to be an array here because we
      // need the API to behave according to the actual data type the user
      // wanted to pass up
      // This is just being done here for convenience in offline mode
      const dataArray = Array.isArray(data) ? data : [data]
      const cached = getCache(route)
      let returnData = []

      for (const datum of dataArray) {
        const predicate = createFilterPredicate(options)
        let index = cached.resources.findIndex(predicate)

        if (index < 0) {
          index = cached.resources.length

          cached.resources.push({})

          cached.total++
          cached.limit++
          cached.count++
        }

        const resource = cached.resources[index]

        cached.resources[index] = platform.api.core.copyObject({ ...resource, ...datum })

        returnData.push(cached.resources[index])
      }

      platform.store.update(store => {
        store.offlineActions.push(() => upsert(platform.api.core.copyObject(data), platform.api.core.copyObject(options)))

        return store
      })

      // Since we process everything in offline mode as an array for convenience
      // we need to make sure to return a single object if it's what the user
      // provided to us in the first place
      if (!Array.isArray(data)) {
        returnData = returnData.pop()
      }

      return Promise.resolve(platform.api.core.copyObject(returnData))
    }

    const queryParams = new URLSearchParams()

    if (options.where) {
      if (typeof options.where !== 'string') {
        throw new Error('where option must be a string')
      }

      queryParams.set('where', options.where)
      queryParams.delete('where-args')

      if (options.whereArgs) {
        const { query, args } = createArgs(options.where, options.whereArgs)

        queryParams.set('where', query)
        queryParams.set('where-args', args.join(','))
      }
    }

    let queryString = queryParams.toString()

    if (queryString) {
      queryString = `?${queryString}`
    }

    let path = ''

    if (!queryString && !Array.isArray(data) && data.id) {
      path = `/${data.id}`
    }

    const res = await platform.fetch(`https://${hostname}${route}${path}${queryString}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  func.route = route

  return func
}

function createRemove (platform, hostname, route, before) {
  const func = async function remove (id, options) {
    if (typeof id !== 'string') {
      options = id
      id = null
    }

    if (typeof before === 'function') {
      await before(options)
    }

    if (!platform.storeData.isOnline) {
      const cached = getCache(route)

      if (!options) {
        throw new Error(`${route}: no id provided`)
      }

      const predicate = createFilterPredicate(options)
      const resources = []

      for (let i = cached.resources.length - 1; i >= 0; i--) {
        const resource = cached.resources[i]

        if (predicate(resource)) {
          cached.resources.splice(i, 1)
          resources.push(resource)

          cached.total--
          cached.limit--
          cached.count--
        }
      }

      platform.store.update(store => {
        store.offlineActions.push(() => remove(id, options))

        return store
      })

      return Promise.resolve(platform.api.core.copyObject(resources))
    }

    let queryString = ''

    if (options) {
      const queryParams = new URLSearchParams()

      if (options.where) {
        if (typeof options.where !== 'string') {
          throw new Error('where option must be a string')
        }

        queryParams.set('where', options.where)
        queryParams.delete('where-args')

        if (options.whereArgs) {
          const { query, args } = createArgs(options.where, options.whereArgs)

          queryParams.set('where', query)
          queryParams.set('where-args', args.join(','))
        }
      }

      if (options.soft) {
        queryParams.set('soft', 1)
      }

      if (options.limit && (options.limit.offset || options.limit.count)) {
        let offset = Number(options.limit.offset) || 0
        let count = Number(options.limit.count) || 10

        if (offset < 0) {
          offset = 0
        }

        if (count < 0) {
          count = 10
        }

        if (count > maxCount) {
          console.warn([
            `${route}:`,
            `the maximum number of resources that can be deleted in a single`,
            `request is ${maxCount}, but this request is attempting to delete ${count}`,
          ].join(' '))
        }

        queryParams.set('limit', `${offset},${count}`)
      }

      queryString = queryParams.toString()

      if (queryString) {
        queryString = `?${queryString}`
      }
    }

    let path = ''

    if (typeof id === 'string' && id) {
      path = `/${id}`
    }

    const res = await platform.fetch(`https://${hostname}${route}${path}${queryString}`, {
      method: 'DELETE',
    })

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  func.route = route

  return func
}

function createSchema (platform, hostname, route) {
  let schema = null

  const func = async function () {
    if (schema) {
      return platform.api.core.copyObject(schema)
    }

    const res = await platform.fetch(`https://${hostname}${route}?schema`)

    await maybeThrowAPIError(res, route)

    schema = res.json()

    return platform.api.core.copyObject(schema)
  }

  func.route = route

  return func
}

function createRoute (platform, hostname, route, before) {
  return {
    route,
    count: createCount(platform, hostname, route, before),
    find: createFind(platform, hostname, route, before),
    findAll: createFindAll(platform, hostname, route, before),
    findAllPages: createFindAllPages(platform, hostname, route, before),
    create: createCreate(platform, hostname, route),
    update: createUpdate(platform, hostname, route),
    upsert: createUpsert(platform, hostname, route, before),
    remove: createRemove(platform, hostname, route, before),
    schema: createSchema(platform, hostname, route),
  }
}

async function maybeThrowAPIError (res, route) {
  if (res.status < 400) {
    return
  }

  const status = res.status
  let message = ''
  let meta = {}
  let code = 0

  try {
    const data = await res.json()

    if (data.message) {
      message = data.message
    }

    if (data.meta) {
      meta = data.meta
    }

    if (data.code) {
      code = data.code
    }
  } catch (err) {}

  const err = new Error(`HTTP status ${status} for '${route}'`)

  err.data = {
    status,
    message,
    meta,
    code,
  }

  throw err
}

function createArgs (query, argsList) {
  if (typeof query !== 'string') {
    throw new Error('query must be a string')
  }

  if (!Array.isArray(argsList)) {
    throw new Error('query args must be an array')
  }

  const placeholders = [...query.matchAll(/\?+/g)].flat()
  const args = []

  for (const [idx, arg] of argsList.entries()) {
    let vals = arg

    if (Array.isArray(vals)) {
      if (placeholders[idx] !== '???') {
        throw new Error(`expected placeholder ${idx} to be ???, found ? instead`)
      }

      // Replace ??? with ? so we can count any ??? that were missed at the end
      placeholders[idx] = '?'

      vals = vals.flat(Infinity)

      // Replace the first instance of ??? with the correct number of ?
      query = query.replace('???', '?'.repeat(vals.length).split('').join(', '))
    } else {
      if (placeholders[idx] !== '?') {
        throw new Error(`expected placeholder ${idx} to be ?, found ??? instead`)
      }

      vals = [vals]
    }

    for (let val of vals) {
      if (typeof val === 'string') {
        val = `'${val.replace(/\\/g, `\\\\`).replace(/'/g, `\\'`)}'`
      } else if (val === null) {
        val = 'NULL'
      } else if (typeof val === 'undefined') {
        val = `''`
      }

      args.push(val)
    }
  }

  for (const [idx, placeholder] of placeholders.entries()) {
    if (placeholder === '???') {
      throw new Error(`missing array arg for placeholder ${idx}`)
    }
  }

  return { query, args }
}

const cache = {}

function getCache (route) {
  const cached = cache[route]

  if (!cached) {
    throw new Error(`no cached resources found for "${route}"`)
  }

  return cached
}

let sqlInterpreter = null

function createSort (options) {
  if (!sqlInterpreter) {
    throw new Error('sqlInterpreter expected')
  }

  if (options.order) {
    return sqlInterpreter.order(options.order, options.orderArgs)
  }

  return null
}

function createFilterPredicate (options) {
  if (!sqlInterpreter) {
    throw new Error('sqlInterpreter expected')
  }

  if (typeof options === 'string') {
    const key = options
    let field = 'id'
    let value = key

    if (key.includes('=')) {
      const parts = key.split('=')

      field = parts.shift()
      value = parts.join('=')
    }

    return r => r[field] === value
  }

  if (options.where) {
    return sqlInterpreter.filter(options.where, options.whereArgs)
  }

  return null
}

async function cacheResources (key, paginated) {
  if (!sqlInterpreter) {
    sqlInterpreter = await import('../api/sql_interpreter.js')
  }

  if (!IS_BROWSER) {
    return
  }

  if (
    !paginated ||
    typeof paginated.total === 'undefined' ||
    typeof paginated.offset === 'undefined' ||
    typeof paginated.limit === 'undefined' ||
    typeof paginated.count === 'undefined' ||
    !Array.isArray(paginated.resources)
  ) {
    throw new Error('cached data must be a valid paginated resource')
  }

  cache[key] = paginated
}

export default {
  createCount,
  createFind,
  createFindAll,
  createFindAllPages,
  createCreate,
  createUpdate,
  createUpsert,
  createRemove,
  createRoute,
  createSchema,
  maybeThrowAPIError,
  cacheResources,
}
